import { Loader2 } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { cn } from '../../lib/utils';
import useResponsive from '../../hooks/useResponsive';
import { NAV, HEADER } from '../../config-global';
import { useAuthContext } from '../../auth/useAuthContext';
import Logo from '../logo';
import { Progress } from '../ui/Progress';
import { useSettingsContext } from '../settings';

// ----------------------------------------------------------------------

export default function LoadingScreen() {
  const { pathname } = useLocation();

  const isDesktop = useResponsive('up', 'lg');

  const { isInitialized } = useAuthContext();

  const { themeLayout } = useSettingsContext();

  const isDashboard = isInitialized && pathname.includes('/dashboard') && isDesktop;

  const size =
    (themeLayout === 'mini' && NAV.W_DASHBOARD_MINI) ||
    (themeLayout === 'vertical' && NAV.W_DASHBOARD) ||
    144;

  // Calculate offset styles for dashboard mode
  const dashboardStyles = isDashboard
    ? {
        width: `calc(100% - ${size}px)`,
        height: `calc(100% - ${HEADER.H_DASHBOARD_DESKTOP}px)`,
        ...(themeLayout === 'horizontal' && {
          width: '100%',
          height: `calc(100% - ${size}px)`
        })
      }
    : {};

  return (
    <>
      {/* Top progress bar */}
      <div className="fixed top-0 left-0 right-0 z-[9999]">
        <Progress value={undefined} className="h-1 rounded-none" />
      </div>

      <div
        className={cn(
          'fixed right-0 bottom-0 z-[9998] flex items-center justify-center bg-background',
          !isDashboard && 'inset-0'
        )}
        style={isDashboard ? dashboardStyles : { width: '100%', height: '100%' }}
      >
        {isDashboard ? (
          <div className="w-full max-w-[360px] h-1 bg-muted overflow-hidden rounded-full">
            <div className="h-full bg-current animate-progress-bar" />
          </div>
        ) : (
          <div className="relative flex items-center justify-center">
            {/* Animated logo */}
            <div className="animate-pulse">
              <Logo disabledLink width={64} height={64} />
            </div>

            {/* Outer spinning border */}
            <div
              className="absolute w-[120px] h-[120px] rounded-[25%] border-[8px] border-primary/25 dark:border-secondary/50 animate-spin-slow"
              style={{ animationDuration: '0.85s' }}
            />

            {/* Inner spinning border */}
            <div
              className="absolute w-[100px] h-[100px] rounded-[25%] border-[3px] border-primary/25 dark:border-secondary/50 animate-spin-reverse"
              style={{ animationDuration: '0.85s' }}
            />
          </div>
        )}
      </div>
    </>
  );
}

// Simple spinner variant for use elsewhere
export function LoadingSpinner({ className }: { className?: string }) {
  return <Loader2 className={cn('h-6 w-6 animate-spin text-primary dark:text-secondary', className)} />;
}
